"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { RESTRICTION_SITE_FINDER_STRINGS } from "@/components/tools/restriction-site-finder";
import { t } from "@/lib/i18n/interpolate";
import {
  type EnzymeResult,
  type Fragment,
  type Hit,
  overhangOf,
} from "@/lib/tool-kit/biology-genetics/restriction";

export type RestrictionStrings = typeof RESTRICTION_SITE_FINDER_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const LISTED_POSITIONS = 8;

export function overhangText(
  result: EnzymeResult,
  strings: RestrictionStrings,
  fmt: Fmt
): string {
  const overhang = overhangOf(result.enzyme);
  if (overhang.type === "blunt") {
    return strings.blunt;
  }
  const template =
    overhang.type === "5prime" ? strings.overhang5 : strings.overhang3;
  return t(template, { length: fmt(overhang.length) });
}

function positions(hits: readonly Hit[], fmt: Fmt, more: string): string {
  const shown = hits.slice(0, LISTED_POSITIONS).map((hit) => fmt(hit.topCut));
  const extra = hits.length - LISTED_POSITIONS;
  return extra > 0
    ? `${shown.join(", ")} ${t(more, { count: fmt(extra) })}`
    : shown.join(", ");
}

export function EnzymeTable({
  results,
  selected,
  onToggle,
  strings,
  fmt,
}: {
  results: readonly EnzymeResult[];
  selected: ReadonlySet<string>;
  onToggle: (name: string, on: boolean) => void;
  strings: RestrictionStrings;
  fmt: Fmt;
}) {
  return (
    <div className="max-h-[28rem] overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead className="sticky top-0 bg-card">
          <tr>
            <th className={TH} scope="col">
              {strings.selectColumn}
            </th>
            <th className={TH} scope="col">
              {strings.enzymeColumn}
            </th>
            <th className={TH} scope="col">
              {strings.siteColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.cutsColumn}
            </th>
            <th className={TH} scope="col">
              {strings.positionsColumn}
            </th>
            <th className={TH} scope="col">
              {strings.endsColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {results.map((result) => (
            <tr key={result.enzyme.name}>
              <td className={TD}>
                <input
                  aria-label={t(strings.selectEnzyme, {
                    name: result.enzyme.name,
                  })}
                  checked={selected.has(result.enzyme.name)}
                  className="accent-primary"
                  onChange={(event) =>
                    onToggle(result.enzyme.name, event.target.checked)
                  }
                  type="checkbox"
                />
              </td>
              <td className={`${TD} font-medium`}>{result.enzyme.name}</td>
              <td className={`${TD} whitespace-nowrap font-mono`}>
                {result.enzyme.notation}
              </td>
              <td className={`${TD} text-right`}>{fmt(result.hits.length)}</td>
              <td className={TD}>
                {positions(result.hits, fmt, strings.morePositions)}
              </td>
              <td className={`${TD} whitespace-nowrap`}>
                {overhangText(result, strings, fmt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FragmentTable({
  rows,
  strings,
  fmt,
}: {
  rows: readonly Fragment[];
  strings: RestrictionStrings;
  fmt: Fmt;
}) {
  const sorted = [...rows].sort((a, b) => b.length - a.length);
  return (
    <div className="max-h-80 overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead className="sticky top-0 bg-card">
          <tr>
            <th className={TH} scope="col">
              {strings.fragmentColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.fromColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.toColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.sizeColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={`${row.start}-${row.end}`}>
              <td className={TD}>{fmt(index + 1)}</td>
              <td className={`${TD} text-right`}>{fmt(row.start)}</td>
              <td className={`${TD} text-right`}>{fmt(row.end)}</td>
              <td className={`${TD} text-right`}>{fmt(row.length)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SiteTable({
  hits,
  strings,
  fmt,
}: {
  hits: readonly Hit[];
  strings: RestrictionStrings;
  fmt: Fmt;
}) {
  return (
    <div className="max-h-80 overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead className="sticky top-0 bg-card">
          <tr>
            <th className={TH} scope="col">
              {strings.enzymeColumn}
            </th>
            <th className={TH} scope="col">
              {strings.strandColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.siteStartColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.siteEndColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.cutAfterColumn}
            </th>
            <th className={TH} scope="col">
              {strings.matchedColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {hits.map((hit) => (
            <tr key={`${hit.enzyme}-${hit.strand}-${hit.start}`}>
              <td className={`${TD} font-medium`}>{hit.enzyme}</td>
              <td className={TD}>{hit.strand}</td>
              <td className={`${TD} text-right`}>{fmt(hit.start + 1)}</td>
              <td className={`${TD} text-right`}>{fmt(hit.end)}</td>
              <td className={`${TD} text-right`}>{fmt(hit.topCut)}</td>
              <td className={`${TD} font-mono`}>{hit.matched}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
